/**
 * Theme cosmetic channel editor for Shop Creator / Crates Creator.
 * Channels: Background, Balls, Buttons, Text, plus Particles.
 */
import { MAX_THEME_GRADIENT_COLORS } from "../../lib/shopTypes";
import {
  THEME_COSMETIC_CHANNEL_KEYS,
  THEME_COSMETIC_CHANNEL_LABELS,
  buildThemeCosmeticPreviewDataUrl,
  emptyThemeEditorState,
  editorStateFromThemeData,
  normalizeThemeCosmeticData,
  themeDataFromEditorState,
  themeCosmeticConfiguredKeys,
  type ThemeCosmeticChannelKey,
  type ThemeCosmeticData,
} from "./theme-cosmetic";
import {
  createThemeEyeButton,
  openThemeLivePreviewModal,
  paintThemeBackgroundPreview,
  startThemeChannelBadgeCycle,
} from "./theme-cosmetic-preview";
import { openThemeDrawOverlay } from "./theme-draw-overlay";
import {
  buildParticleDecoration,
  openParticleSetupModal,
  particlePlacementFromScreens,
} from "./theme-particle-setup";

export type ThemeCosmeticEditorApi = {
  root: HTMLElement;
  getThemeData: () => ThemeCosmeticData | null;
  setThemeData: (data: ThemeCosmeticData | null) => void;
  getPreviewDataUrl: () => string;
  onChange: (fn: () => void) => void;
};

export type ThemeCosmeticEditorOpts = {
  /** Leave Admin and preview the draft Theme on Home (full navigation / play). */
  onEnterNavigablePreview?: (api: {
    getData: () => ThemeCosmeticData | null;
    setData: (data: ThemeCosmeticData | null) => void;
    onExit: () => void;
  }) => void;
};

export function mountThemeCosmeticEditor(
  parent: HTMLElement,
  opts?: ThemeCosmeticEditorOpts,
): ThemeCosmeticEditorApi {
  const root = document.createElement("div");
  root.className = "adminShopThemeEditor modeBox hidden";

  const title = document.createElement("span");
  title.className = "adminShopToolbarLabel";
  title.textContent = "Theme channels (enable only what this item should change)";
  root.appendChild(title);

  const preview = document.createElement("div");
  preview.className = "adminShopThemePreview";

  const labelRow = document.createElement("div");
  labelRow.className = "themeCosmeticLabelRow";
  const badge = document.createElement("span");
  badge.className = "shopCosmeticTrailBadge shopCosmeticThemeBadge themeCosmeticCycleBadge";
  labelRow.appendChild(badge);
  preview.appendChild(labelRow);

  const previewFrame = document.createElement("div");
  previewFrame.className = "adminShopThemePreviewFrame";
  const previewCanvas = document.createElement("canvas");
  previewCanvas.className = "adminShopThemePreviewCanvas";
  previewCanvas.width = 160;
  previewCanvas.height = 160;
  previewCanvas.setAttribute("aria-label", "Theme background preview");
  previewFrame.appendChild(previewCanvas);
  preview.appendChild(previewFrame);
  root.appendChild(preview);

  let state = emptyThemeEditorState();
  let decorations: NonNullable<ThemeCosmeticData["decorations"]> = [];
  let particlesEnabled = false;
  let changeFn: (() => void) | null = null;
  let stopBadgeCycle: (() => void) | null = null;
  const channelRoots = new Map<ThemeCosmeticChannelKey, HTMLElement>();

  function activeDecorations(): ThemeCosmeticData["decorations"] {
    return particlesEnabled ? decorations.filter((d) => d.kind === "particles") : undefined;
  }

  function buildThemeData(): ThemeCosmeticData | null {
    return themeDataFromEditorState(state, activeDecorations());
  }

  function badgeLabels(data: ThemeCosmeticData | null): string[] {
    if (!data) return [];
    const labels = themeCosmeticConfiguredKeys(data).map((k) => THEME_COSMETIC_CHANNEL_LABELS[k]);
    if (data.decorations?.some((d) => d.kind === "particles")) labels.push("Particles");
    return labels;
  }

  // Eye button: top-left of the creator screen while Theme editor is visible.
  const eyeBtn = createThemeEyeButton(() => {
    const data = buildThemeData();
    if (data) openThemeLivePreviewModal(data);
  });
  eyeBtn.classList.add("themeCosmeticEyeBtn--creator");
  eyeBtn.classList.add("hidden");
  if (getComputedStyle(parent).position === "static") {
    parent.style.position = "relative";
  }
  parent.appendChild(eyeBtn);

  const previewNavRow = document.createElement("div");
  previewNavRow.className = "adminShopThemePreviewNavRow";
  const openPreviewBtn = document.createElement("button");
  openPreviewBtn.type = "button";
  openPreviewBtn.className = "btn green";
  openPreviewBtn.textContent = "Preview";
  openPreviewBtn.title = "Open Home with this Theme — play freely; records & Dots off";
  openPreviewBtn.onclick = () => {
    if (!opts?.onEnterNavigablePreview) return;
    const data = buildThemeData();
    if (!data) {
      openPreviewBtn.textContent = "Enable a channel or Particles first";
      window.setTimeout(() => {
        openPreviewBtn.textContent = "Preview";
      }, 1600);
      return;
    }
    opts.onEnterNavigablePreview({
      getData: () => buildThemeData(),
      setData: (next) => {
        applyThemeData(next);
      },
      onExit: () => {
        /* Shop Creator stays mounted; panel reopened by host */
      },
    });
  };
  previewNavRow.appendChild(openPreviewBtn);
  if (opts?.onEnterNavigablePreview) {
    root.appendChild(previewNavRow);
  }

  const eyeObs = new MutationObserver(() => {
    const hidden = root.classList.contains("hidden") || !root.isConnected;
    eyeBtn.classList.toggle("hidden", hidden);
  });
  eyeObs.observe(root, { attributes: true, attributeFilter: ["class"] });

  function notify() {
    refreshPreview();
    changeFn?.();
  }

  function refreshPreview() {
    const data = buildThemeData();
    paintThemeBackgroundPreview(previewCanvas, data);
    stopBadgeCycle?.();
    stopBadgeCycle = startThemeChannelBadgeCycle(badge, badgeLabels(data));
    eyeBtn.disabled = !data;
  }

  function renderChannelColors(key: ThemeCosmeticChannelKey, listEl: HTMLElement) {
    const s = state[key];
    listEl.replaceChildren();
    s.colors.forEach((color, index) => {
      const row = document.createElement("div");
      row.className = "adminShopTrailColorRow";
      const inp = document.createElement("input");
      inp.type = "color";
      inp.value = color;
      inp.oninput = () => {
        state[key].colors[index] = inp.value;
        notify();
      };
      row.appendChild(inp);
      listEl.appendChild(row);
    });
  }

  for (const key of THEME_COSMETIC_CHANNEL_KEYS) {
    const box = document.createElement("div");
    box.className = "adminShopThemeChannel";
    channelRoots.set(key, box);

    const head = document.createElement("label");
    head.className = "adminShopThemeChannelHead";
    const enable = document.createElement("input");
    enable.type = "checkbox";
    enable.checked = state[key].enabled;
    const headText = document.createElement("span");
    headText.textContent = THEME_COSMETIC_CHANNEL_LABELS[key];
    head.appendChild(enable);
    head.appendChild(headText);
    box.appendChild(head);

    const body = document.createElement("div");
    body.className = "adminShopThemeChannelBody";

    const modeRow = document.createElement("div");
    modeRow.className = "adminShopThemeModeRow";
    const modeSelect = document.createElement("select");
    modeSelect.className = "formInput adminShopToolSelect";
    (
      [
        ["solid", "Solid color"],
        ["gradient", "Gradient"],
      ] as const
    ).forEach(([v, label]) => {
      const o = document.createElement("option");
      o.value = v;
      o.textContent = label;
      modeSelect.appendChild(o);
    });
    modeSelect.value = state[key].mode;
    modeRow.appendChild(modeSelect);
    body.appendChild(modeRow);

    const colorsList = document.createElement("div");
    colorsList.className = "adminShopTrailColorsList";
    body.appendChild(colorsList);

    const colorActions = document.createElement("div");
    colorActions.className = "adminShopTrailColorActions";
    const addBtn = document.createElement("button");
    addBtn.type = "button";
    addBtn.className = "btn btnSmall";
    addBtn.textContent = "Add color";
    const removeBtn = document.createElement("button");
    removeBtn.type = "button";
    removeBtn.className = "btn btnSmall";
    removeBtn.textContent = "Remove";
    colorActions.appendChild(addBtn);
    colorActions.appendChild(removeBtn);
    body.appendChild(colorActions);

    const angleRow = document.createElement("div");
    angleRow.className = "adminShopThemeAngleRow";
    const angleLabel = document.createElement("span");
    angleLabel.className = "adminShopToolbarLabel";
    angleLabel.textContent = "Angle";
    const angleInput = document.createElement("input");
    angleInput.type = "range";
    angleInput.min = "0";
    angleInput.max = "359";
    angleInput.value = String(state[key].angleDeg);
    const angleVal = document.createElement("span");
    angleVal.className = "adminShopThemeAngleVal";
    angleVal.textContent = `${state[key].angleDeg}°`;
    angleRow.appendChild(angleLabel);
    angleRow.appendChild(angleInput);
    angleRow.appendChild(angleVal);
    body.appendChild(angleRow);

    const gradPreview = document.createElement("div");
    gradPreview.className = "adminShopTrailGradientPreview";
    body.appendChild(gradPreview);

    function syncChannelUi() {
      const s = state[key];
      enable.checked = s.enabled;
      body.classList.toggle("hidden", !s.enabled);
      modeSelect.value = s.mode;
      const isGrad = s.mode === "gradient";
      colorActions.classList.toggle("hidden", !isGrad);
      angleRow.classList.toggle("hidden", !isGrad);
      gradPreview.classList.toggle("hidden", !isGrad);
      if (!s.colors.length) s.colors = ["#ffffff"];
      renderChannelColors(key, colorsList);
      if (!isGrad) {
        colorsList.replaceChildren();
        const row = document.createElement("div");
        row.className = "adminShopTrailColorRow";
        const inp = document.createElement("input");
        inp.type = "color";
        inp.value = s.colors[0];
        inp.oninput = () => {
          state[key].colors = [inp.value];
          notify();
        };
        row.appendChild(inp);
        colorsList.appendChild(row);
      }
      addBtn.disabled = s.colors.length >= MAX_THEME_GRADIENT_COLORS;
      removeBtn.disabled = s.colors.length <= 1;
      angleInput.value = String(s.angleDeg);
      angleVal.textContent = `${s.angleDeg}°`;
      if (isGrad && s.colors.length) {
        const stops = s.colors
          .map((c, i) => `${c} ${(i / Math.max(1, s.colors.length - 1)) * 100}%`)
          .join(", ");
        gradPreview.style.background = `linear-gradient(${s.angleDeg}deg, ${stops})`;
      } else {
        gradPreview.style.background = s.colors[0] || "#000";
      }
    }

    enable.onchange = () => {
      state[key].enabled = enable.checked;
      if (state[key].enabled && !state[key].colors.length) {
        state[key].colors = emptyThemeEditorState()[key].colors.slice();
      }
      syncChannelUi();
      notify();
    };
    modeSelect.onchange = () => {
      state[key].mode = modeSelect.value === "gradient" ? "gradient" : "solid";
      if (state[key].mode === "gradient" && state[key].colors.length < 2) {
        state[key].colors = [state[key].colors[0] || "#ffffff", "#66ffff"];
      }
      if (state[key].mode === "solid") {
        state[key].colors = [state[key].colors[0] || "#ffffff"];
      }
      syncChannelUi();
      notify();
    };
    addBtn.onclick = () => {
      if (state[key].colors.length >= MAX_THEME_GRADIENT_COLORS) return;
      state[key].colors.push("#ffffff");
      syncChannelUi();
      notify();
    };
    removeBtn.onclick = () => {
      if (state[key].colors.length <= 1) return;
      state[key].colors.pop();
      syncChannelUi();
      notify();
    };
    angleInput.oninput = () => {
      state[key].angleDeg = Math.round(Number(angleInput.value) || 0);
      angleVal.textContent = `${state[key].angleDeg}°`;
      notify();
      syncChannelUi();
    };

    box.appendChild(body);
    root.appendChild(box);
    syncChannelUi();
  }

  // Particles channel — enable + Edit opens Particle System setup.
  const particlesBox = document.createElement("div");
  particlesBox.className = "adminShopThemeChannel adminShopThemeParticlesChannel";
  const particlesHead = document.createElement("label");
  particlesHead.className = "adminShopThemeChannelHead";
  const particlesEnable = document.createElement("input");
  particlesEnable.type = "checkbox";
  particlesEnable.checked = particlesEnabled;
  const particlesHeadText = document.createElement("span");
  particlesHeadText.textContent = "Particles";
  particlesHead.appendChild(particlesEnable);
  particlesHead.appendChild(particlesHeadText);
  particlesBox.appendChild(particlesHead);

  const particlesBody = document.createElement("div");
  particlesBody.className = "adminShopThemeChannelBody";
  const particlesHint = document.createElement("span");
  particlesHint.className = "adminShopToolbarLabel adminShopThemeParticlesHint";
  const particlesEdit = document.createElement("button");
  particlesEdit.type = "button";
  particlesEdit.className = "btn btnSmall";
  particlesEdit.textContent = "Edit";
  particlesBody.appendChild(particlesHint);
  particlesBody.appendChild(particlesEdit);
  particlesBox.appendChild(particlesBody);
  root.appendChild(particlesBox);

  function syncParticlesUi() {
    particlesEnable.checked = particlesEnabled;
    particlesBody.classList.toggle("hidden", !particlesEnabled);
    const count = decorations.filter((d) => d.kind === "particles").length;
    particlesHint.textContent = count
      ? `${count} particle system${count === 1 ? "" : "s"} — press Edit to change`
      : "Not set — press Edit to configure";
    particlesEdit.disabled = !particlesEnabled;
  }

  function openParticleArt(
    screens: Parameters<typeof buildParticleDecoration>[0]["screens"],
    config: Parameters<typeof buildParticleDecoration>[0]["config"],
    replaceId?: string,
  ) {
    const bg =
      (state.background.enabled && state.background.colors[0]) ||
      getComputedStyle(document.documentElement).getPropertyValue("--menu-bg-raw").trim() ||
      "#1a1a2e";
    openThemeDrawOverlay({
      mode: "static",
      title: "Particle art",
      surface: "canvas",
      backgroundColor: bg,
      onCancel: () => {},
      onConfirm: (result) => {
        const dec = buildParticleDecoration({
          imageData: result.imageData,
          opacity: result.opacity,
          screens,
          config,
        });
        if (replaceId) dec.id = replaceId;
        const normalized = normalizeThemeCosmeticData({ decorations: [dec] });
        decorations = normalized?.decorations?.length
          ? [...normalized.decorations]
          : [dec];
        particlesEnabled = true;
        syncParticlesUi();
        notify();
      },
    });
  }

  particlesEnable.onchange = () => {
    particlesEnabled = particlesEnable.checked;
    syncParticlesUi();
    notify();
  };

  particlesEdit.onclick = () => {
    if (!particlesEnabled) return;
    void (async () => {
      const existing = decorations.find((d) => d.kind === "particles");
      const setup = await openParticleSetupModal(
        existing
          ? {
              screens: existing.screens,
              config: existing.particleConfig,
              confirmLabel: "Save Changes",
              artLabel: "Edit art",
            }
          : undefined,
      );
      if (!setup) return;
      if (existing && !setup.editArt) {
        const place = particlePlacementFromScreens(setup.screens);
        decorations = [
          {
            ...existing,
            screens: setup.screens,
            particleConfig: setup.config,
            anchor: place.anchor,
            layer: place.layer,
          },
        ];
        syncParticlesUi();
        notify();
        return;
      }
      openParticleArt(setup.screens, setup.config, existing?.id);
    })();
  };

  syncParticlesUi();

  function applyThemeData(data: ThemeCosmeticData | null) {
    state = editorStateFromThemeData(data);
    decorations = [...(data?.decorations || [])].filter((d) => d.kind === "particles");
    particlesEnabled = decorations.length > 0;
    for (const key of THEME_COSMETIC_CHANNEL_KEYS) {
      const el = channelRoots.get(key);
      if (!el) continue;
      const s = state[key];
      const enable = el.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
      if (enable) {
        enable.checked = s.enabled;
        enable.dispatchEvent(new Event("change"));
      }
      const modeSelect = el.querySelector("select") as HTMLSelectElement | null;
      if (modeSelect) {
        modeSelect.value = s.mode;
        modeSelect.dispatchEvent(new Event("change"));
      }
    }
    // Re-apply exact colour state after dispatch side-effects
    state = editorStateFromThemeData(data);
    for (const key of THEME_COSMETIC_CHANNEL_KEYS) {
      const el = channelRoots.get(key);
      if (!el) continue;
      const s = state[key];
      const enable = el.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
      const modeSelect = el.querySelector("select") as HTMLSelectElement | null;
      const angleInput = el.querySelector('input[type="range"]') as HTMLInputElement | null;
      const angleVal = el.querySelector(".adminShopThemeAngleVal") as HTMLElement | null;
      const body = el.querySelector(".adminShopThemeChannelBody") as HTMLElement | null;
      const colorActions = el.querySelector(".adminShopTrailColorActions") as HTMLElement | null;
      const angleRow = el.querySelector(".adminShopThemeAngleRow") as HTMLElement | null;
      const gradPreview = el.querySelector(".adminShopTrailGradientPreview") as HTMLElement | null;
      const colorsList = el.querySelector(".adminShopTrailColorsList") as HTMLElement | null;
      if (enable) enable.checked = s.enabled;
      if (body) body.classList.toggle("hidden", !s.enabled);
      if (modeSelect) modeSelect.value = s.mode;
      const isGrad = s.mode === "gradient";
      colorActions?.classList.toggle("hidden", !isGrad);
      angleRow?.classList.toggle("hidden", !isGrad);
      gradPreview?.classList.toggle("hidden", !isGrad);
      if (angleInput) angleInput.value = String(s.angleDeg);
      if (angleVal) angleVal.textContent = `${s.angleDeg}°`;
      if (colorsList) {
        colorsList.replaceChildren();
        const colors = isGrad ? s.colors : [s.colors[0] || "#ffffff"];
        colors.forEach((color, index) => {
          const row = document.createElement("div");
          row.className = "adminShopTrailColorRow";
          const inp = document.createElement("input");
          inp.type = "color";
          inp.value = color;
          inp.oninput = () => {
            if (isGrad) state[key].colors[index] = inp.value;
            else state[key].colors = [inp.value];
            notify();
          };
          row.appendChild(inp);
          colorsList.appendChild(row);
        });
      }
      if (gradPreview && isGrad && s.colors.length) {
        const stops = s.colors
          .map((c, i) => `${c} ${(i / Math.max(1, s.colors.length - 1)) * 100}%`)
          .join(", ");
        gradPreview.style.background = `linear-gradient(${s.angleDeg}deg, ${stops})`;
      }
    }
    syncParticlesUi();
    refreshPreview();
  }

  parent.appendChild(root);
  refreshPreview();

  return {
    root,
    getThemeData: () => buildThemeData(),
    setThemeData: (data) => {
      applyThemeData(data);
    },
    getPreviewDataUrl: () => {
      const data = buildThemeData();
      return data ? buildThemeCosmeticPreviewDataUrl(data) : "";
    },
    onChange: (fn) => {
      changeFn = fn;
    },
  };
}
